import { normalizeCareTimeLabel } from '../../../shared/careDateTimePatterns';

const normalizeComparableText = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const getRowDate = (row = {}) => {
  const value = row?.date || null;
  const date = value instanceof Date ? value : (value ? new Date(value) : null);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const getDateKey = (row = {}) => {
  const date = getRowDate(row);
  if (!date) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const getTimeKey = (row = {}) => String(
  row?.localTime
  || row?.time
  || row?.timeLabel
  || row?.metadata?.timeLabel
  || ''
).trim().toLowerCase();

const normalizeTimeKey = (value = '') => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';

  const twentyFourHourMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHourMatch) {
    const hours24 = Number(twentyFourHourMatch[1]);
    const minutes = twentyFourHourMatch[2];
    if (Number.isFinite(hours24) && hours24 >= 0 && hours24 <= 23) {
      const meridiem = hours24 >= 12 ? 'pm' : 'am';
      const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
      return `${hours12}:${minutes} ${meridiem}`;
    }
  }

  return normalizeCareTimeLabel(raw).toLowerCase();
};

const getRowBodyText = (row = {}) => String(
  row?.body
  || row?.note
  || row?.text
  || row?.content
  || ''
).trim();

const getSourceBlockId = (row = {}) => String(
  row?.sourceBlockId
  || row?.metadata?.sourceBlockId
  || ''
).trim();

const rowsLookEquivalent = (localRow = {}, aiRow = {}) => {
  const localSourceBlockId = getSourceBlockId(localRow);
  const aiSourceBlockId = getSourceBlockId(aiRow);
  if (localSourceBlockId && aiSourceBlockId) {
    return localSourceBlockId === aiSourceBlockId;
  }

  const localDateKey = getDateKey(localRow);
  const aiDateKey = getDateKey(aiRow);
  if (!localDateKey || !aiDateKey || localDateKey !== aiDateKey) {
    return false;
  }

  const localTimeKey = getTimeKey(localRow);
  const aiTimeKey = getTimeKey(aiRow);
  const normalizedLocalTimeKey = normalizeTimeKey(localTimeKey);
  const normalizedAiTimeKey = normalizeTimeKey(aiTimeKey);
  if (normalizedLocalTimeKey && normalizedAiTimeKey && normalizedLocalTimeKey !== normalizedAiTimeKey) {
    return false;
  }
  if (normalizedLocalTimeKey && normalizedAiTimeKey && normalizedLocalTimeKey === normalizedAiTimeKey) {
    return true;
  }

  const localText = normalizeComparableText(getRowBodyText(localRow));
  const aiText = normalizeComparableText(getRowBodyText(aiRow));
  if (!localText || !aiText) {
    return false;
  }

  return (
    localText === aiText
    || localText.includes(aiText)
    || aiText.includes(localText)
  );
};

const sortRowsChronologically = (rows = []) => [...rows].sort((left, right) => {
  const leftDate = getRowDate(left);
  const rightDate = getRowDate(right);
  if (!leftDate && !rightDate) return 0;
  if (!leftDate) return 1;
  if (!rightDate) return -1;
  return leftDate.getTime() - rightDate.getTime();
});

export const recoverMissingLocalRowsFromAISegments = ({
  localRows = [],
  aiRows = [],
} = {}) => {
  if (!Array.isArray(localRows) || localRows.length === 0 || !Array.isArray(aiRows) || aiRows.length === 0) {
    return aiRows;
  }

  const recoveredLocalRows = localRows
    .filter((localRow) => {
      const localDateKey = getDateKey(localRow);
      const localText = normalizeComparableText(getRowBodyText(localRow));
      if (!localDateKey || !localText) {
        return false;
      }

      return !aiRows.some((aiRow) => rowsLookEquivalent(localRow, aiRow));
    })
    .map((localRow) => ({
      ...localRow,
      metadata: {
        ...(localRow?.metadata || {}),
        aiTimelineRecoveredFromLocal: true,
      },
    }));

  if (recoveredLocalRows.length === 0) {
    return aiRows;
  }

  return sortRowsChronologically([...aiRows, ...recoveredLocalRows]);
};
